/**
 * ElevenLabs Service
 * 
 * Handles interactions with the ElevenLabs API for voice suggestions.
 */

const ELEVENLABS_API_BASE = 'https://api.elevenlabs.io/v1'

/**
 * Fetch all available voices from ElevenLabs
 * @param {string} apiKey - ElevenLabs API key
 * @returns {Promise<Array>} List of available voices
 */
export async function getVoices(apiKey) {
  if (!apiKey) {
    throw new Error('ElevenLabs API key is required')
  }

  const response = await fetch(`${ELEVENLABS_API_BASE}/voices`, {
    method: 'GET',
    headers: {
      'xi-api-key': apiKey,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Invalid API key. Please check your ElevenLabs API key.')
    }
    throw new Error(`Failed to fetch voices: ${response.status}`)
  }

  const data = await response.json()
  return data.voices || []
}

/**
 * Keywords that suggest different voice characteristics
 */
const VOICE_KEYWORDS = {
  // Gender hints
  male: ['he', 'his', 'him', 'man', 'boy', 'male', 'gentleman', 'father', 'brother', 'son', 'king', 'prince', 'lord', 'sir'],
  female: ['she', 'her', 'woman', 'girl', 'female', 'lady', 'mother', 'sister', 'daughter', 'queen', 'princess', 'madam'],
  
  // Age hints
  young: ['young', 'child', 'kid', 'teen', 'teenager', 'youthful', 'boy', 'girl', 'juvenile'],
  old: ['old', 'elderly', 'aged', 'senior', 'ancient', 'wise', 'gray', 'grey', 'wrinkled', 'grandfather', 'grandmother'],
  middleAged: ['middle-aged', 'mature', 'adult'],
  
  // Personality/tone hints
  warm: ['warm', 'friendly', 'kind', 'gentle', 'caring', 'nurturing', 'compassionate', 'loving'],
  authoritative: ['authoritative', 'commanding', 'powerful', 'strong', 'leader', 'boss', 'chief', 'captain', 'general'],
  mysterious: ['mysterious', 'enigmatic', 'secretive', 'shadowy', 'dark', 'cryptic'],
  cheerful: ['cheerful', 'happy', 'joyful', 'upbeat', 'energetic', 'enthusiastic', 'bubbly'],
  serious: ['serious', 'stern', 'grave', 'solemn', 'stoic'],
  playful: ['playful', 'mischievous', 'witty', 'humorous', 'sarcastic'],
  
  // Accent/origin hints
  british: ['british', 'english', 'london', 'uk', 'royal', 'aristocrat', 'posh'],
  american: ['american', 'usa', 'new york', 'southern', 'texas'],
  european: ['european', 'french', 'german', 'italian', 'spanish'],
}

/**
 * Analyze character data to extract voice matching criteria
 * @param {object} character - Character data
 * @returns {object} Voice matching criteria
 */
function analyzeCharacter(character) {
  const criteria = {
    gender: null,
    age: null,
    traits: [],
    accentHints: [],
  }

  // Combine all text fields for analysis
  const allText = [
    character.name || '',
    character.description || '',
    character.attire || '',
    character.hairStyle || '',
    character.distinctFeatures || '',
    character.accessories || '',
  ].join(' ').toLowerCase()

  // Detect gender
  const maleMatches = VOICE_KEYWORDS.male.filter(k => allText.includes(k)).length
  const femaleMatches = VOICE_KEYWORDS.female.filter(k => allText.includes(k)).length
  if (maleMatches > femaleMatches) criteria.gender = 'male'
  else if (femaleMatches > maleMatches) criteria.gender = 'female'

  // Detect age
  const youngMatches = VOICE_KEYWORDS.young.filter(k => allText.includes(k)).length
  const oldMatches = VOICE_KEYWORDS.old.filter(k => allText.includes(k)).length
  const middleMatches = VOICE_KEYWORDS.middleAged.filter(k => allText.includes(k)).length
  if (youngMatches > oldMatches && youngMatches > middleMatches) criteria.age = 'young'
  else if (oldMatches > youngMatches && oldMatches > middleMatches) criteria.age = 'old'
  else if (middleMatches > 0) criteria.age = 'middle_aged'

  // Detect personality traits
  Object.entries(VOICE_KEYWORDS).forEach(([trait, keywords]) => {
    if (['male', 'female', 'young', 'old', 'middleAged'].includes(trait)) return
    if (['british', 'american', 'european'].includes(trait)) {
      // Handle accents separately
      if (keywords.some(k => allText.includes(k))) {
        criteria.accentHints.push(trait)
      }
    } else {
      if (keywords.some(k => allText.includes(k))) {
        criteria.traits.push(trait)
      }
    }
  })

  return criteria
}

/**
 * Score how well a voice matches the character criteria
 * @param {object} voice - ElevenLabs voice object
 * @param {object} criteria - Character matching criteria
 * @returns {object} Score and match reasons
 */
function scoreVoiceMatch(voice, criteria) {
  let score = 0.5 // Base score
  const reasons = []
  
  const voiceLabels = voice.labels || {}
  const voiceDesc = (voice.description || '').toLowerCase()
  const voiceName = (voice.name || '').toLowerCase()

  // Gender matching
  if (criteria.gender && voiceLabels.gender) {
    if (voiceLabels.gender.toLowerCase() === criteria.gender) {
      score += 0.25
      reasons.push(`${criteria.gender} voice`)
    } else {
      score -= 0.3
    }
  }

  // Age matching
  if (criteria.age && voiceLabels.age) {
    const voiceAge = voiceLabels.age.toLowerCase()
    if (criteria.age === 'young' && (voiceAge.includes('young') || voiceAge.includes('child'))) {
      score += 0.15
      reasons.push('young voice')
    } else if (criteria.age === 'old' && (voiceAge.includes('old') || voiceAge.includes('mature'))) {
      score += 0.15
      reasons.push('mature voice')
    } else if (criteria.age === 'middle_aged' && voiceAge.includes('middle')) {
      score += 0.15
      reasons.push('middle-aged voice')
    }
  }

  // Trait matching
  criteria.traits.forEach(trait => {
    const searchTerms = VOICE_KEYWORDS[trait] || [trait]
    if (searchTerms.some(term => voiceDesc.includes(term) || voiceName.includes(term))) {
      score += 0.1
      reasons.push(`${trait} tone`)
    }
  })

  // Accent matching
  if (voiceLabels.accent) {
    const voiceAccent = voiceLabels.accent.toLowerCase()
    criteria.accentHints.forEach(hint => {
      if (voiceAccent.includes(hint) || hint.includes(voiceAccent)) {
        score += 0.1
        reasons.push(`${voiceLabels.accent} accent`)
      }
    })
  }

  // Use case matching - prefer 'narration' or 'characters' for story characters
  if (voiceLabels.use_case) {
    const useCase = voiceLabels.use_case.toLowerCase()
    if (useCase.includes('narration') || useCase.includes('characters') || useCase.includes('animation')) {
      score += 0.1
      reasons.push('suited for characters')
    }
  }

  // Clamp score between 0 and 1
  score = Math.max(0, Math.min(1, score))

  return { score, reasons }
}

/**
 * Generate recommended voice settings based on character traits
 * @param {object} criteria - Character matching criteria
 * @returns {object} Recommended voice settings
 */
function getRecommendedSettings(criteria) {
  const settings = {
    stability: 0.5,
    similarityBoost: 0.75,
    style: 0.5,
  }

  // More stability for authoritative/serious characters
  if (criteria.traits.includes('authoritative') || criteria.traits.includes('serious')) {
    settings.stability = 0.7
    settings.style = 0.3
  }

  // More expressiveness for cheerful/playful characters
  if (criteria.traits.includes('cheerful') || criteria.traits.includes('playful')) {
    settings.stability = 0.4
    settings.style = 0.7
  }

  // More warmth for warm/gentle characters
  if (criteria.traits.includes('warm')) {
    settings.similarityBoost = 0.8
    settings.style = 0.6
  }

  // More mysterious tone
  if (criteria.traits.includes('mysterious')) {
    settings.stability = 0.6
    settings.similarityBoost = 0.6
    settings.style = 0.4
  }

  return settings
}

/**
 * Suggest voices for a character based on their profile
 * @param {string} apiKey - ElevenLabs API key
 * @param {object} character - Character data
 * @returns {Promise<object>} Voice suggestions
 */
export async function suggestVoicesForCharacter(apiKey, character) {
  // Fetch available voices
  const voices = await getVoices(apiKey)
  
  if (!voices || voices.length === 0) {
    return {
      suggestions: [],
      criteria: {},
      error: 'No voices available',
    }
  }

  // Analyze character to determine matching criteria
  const criteria = analyzeCharacter(character)
  
  // Score each voice
  const scoredVoices = voices.map(voice => {
    const { score, reasons } = scoreVoiceMatch(voice, criteria)
    return {
      voiceId: voice.voice_id,
      voiceName: voice.name,
      category: voice.category || 'premade',
      description: voice.description || 'No description available',
      labels: voice.labels || {},
      previewUrl: voice.preview_url,
      matchScore: score,
      matchReasons: reasons,
      recommendedSettings: getRecommendedSettings(criteria),
    }
  })

  // Sort by score and take top suggestions
  const sorted = scoredVoices.sort((a, b) => b.matchScore - a.matchScore)
  const topSuggestions = sorted.slice(0, 6)

  // Build match reason text
  const suggestions = topSuggestions.map(voice => ({
    ...voice,
    matchReason: voice.matchReasons.length > 0 
      ? `Matches: ${voice.matchReasons.join(', ')}`
      : 'General match based on voice characteristics',
  }))

  return {
    suggestions,
    criteria,
    totalVoices: voices.length,
  }
}

/**
 * Check if character has enough information for voice suggestions
 * @param {object} character - Character data
 * @returns {object} { canSuggest: boolean, missingInfo: string[] }
 */
export function canSuggestVoice(character) {
  if (!character) {
    return { canSuggest: false, missingInfo: ['Character data required'] }
  }

  const missingInfo = []

  // Name is required
  if (!character.name?.trim()) {
    missingInfo.push('name')
  }

  // Need either a good description OR at least 2 detail fields filled
  const hasDescription = character.description?.trim()?.length >= 20
  const detailFields = ['attire', 'hairStyle', 'distinctFeatures', 'accessories']
  const filledDetails = detailFields.filter(f => character[f]?.trim()?.length > 0).length

  if (!hasDescription && filledDetails < 2) {
    if (!hasDescription) {
      missingInfo.push('description (20+ characters)')
    }
    if (filledDetails < 2) {
      missingInfo.push(`at least ${2 - filledDetails} more detail field(s)`)
    }
  }

  return {
    canSuggest: missingInfo.length === 0,
    missingInfo,
  }
}

export default {
  getVoices,
  suggestVoicesForCharacter,
  canSuggestVoice,
}
